<template>
  <div class="getting-started-page container py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10">
        <!-- Header -->
        <div class="text-center mb-5">
          <h1 class="display-4 fw-bold mb-3">
            Willkommen bei<br>
            <img src="/src/assets/img/quitly-blue.svg" alt="Quitly" class="mt-2" style="height: 60px;">
          </h1>
          <p class="lead text-muted">
            Lerne die wichtigsten Funktionen kennen und erstelle deine erste Quittung
          </p>
        </div>

        <!-- Progress Indicator -->
        <div class="card mb-5">
          <div class="card-body">
            <div class="gs-progress-steps">
              <div 
                v-for="(step, index) in steps" 
                :key="index"
                class="gs-step"
                :class="{ 
                  'gs-completed': currentStep > index,
                  'gs-active': currentStep === index,
                  'gs-disabled': currentStep < index
                }"
              >
                <div class="gs-step-circle">
                  <font-awesome-icon 
                    v-if="currentStep > index" 
                    icon="check" 
                    class="gs-step-icon"
                  />
                  <span v-else class="gs-step-number">{{ index + 1 }}</span>
                </div>
                <div class="gs-step-content">
                  <h6 class="gs-step-title">{{ step.title }}</h6>
                  <p class="gs-step-description">{{ step.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step Content -->
        <div class="card">
          <div class="card-body p-4">
            <!-- Step 1: Introduction -->
            <div v-if="currentStep === 0" class="gs-step-content">
              <div class="text-center mb-4">
                <font-awesome-icon icon="info-circle" size="4x" class="text-primary mb-3" />
                <h2>Was ist Quitly?</h2>
                <p class="lead">
                  Quitly ist eine moderne Plattform zur digitalen Quittungserstellung mit 
                  Bereich-Funktionen und automatischer Verwaltung.
                </p>
              </div>
              
              <div class="row g-4">
                <div class="col-md-4">
                  <div class="gs-feature-card text-center">
                    <font-awesome-icon icon="receipt" size="3x" class="text-primary mb-3" />
                    <h5>Digitale Quittungen</h5>
                    <p class="text-muted">
                      Erstelle professionelle Quittungen mit dem integrierten Wizard
                    </p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="gs-feature-card text-center">
                    <font-awesome-icon icon="users" size="3x" class="text-success mb-3" />
                    <h5>Bereich-Kollaboration</h5>
                    <p class="text-muted">
                      Arbeite mit deinem Bereich zusammen und verwalte Quittungen zentral
                    </p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="gs-feature-card text-center">
                    <font-awesome-icon icon="cog" size="3x" class="text-warning mb-3" />
                    <h5>Anpassbar</h5>
                    <p class="text-muted">
                      Individualisiere Templates, Felder und Quittungsnummern
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Bereiche -->
            <div v-if="currentStep === 1" class="gs-step-content">
              <div class="text-center mb-4">
                <font-awesome-icon icon="users" size="4x" class="text-success mb-3" />
                <h2>Bereiche verstehen</h2>
                <p class="lead">
                  Bereiche ermöglichen es dir, mit Kollegen zusammenzuarbeiten und Quittungen zu verwalten
                </p>
              </div>

              <div class="row g-4 mb-4">
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="plus-circle" class="me-2 text-primary" />Bereich erstellen</h5>
                    <p>Erstelle einen neuen Bereich und lade Kollegen ein</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Bereichsname und Beschreibung</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Quittungsnummer-Format definieren</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Benutzerfelder erstellen</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="user-plus" class="me-2 text-success" />Bereich beitreten</h5>
                    <p>Tritt einem bestehenden Bereich bei</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Mit Einladungscode beitreten</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Auf Einladung warten</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Mitgliederfelder ausfüllen</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Zentrale Benutzerfelder-Erklärung -->
              <div class="mt-4">
                <div class="card border-info">
                  <div class="card-body">
                    <h5 class="card-title text-info">
                      <font-awesome-icon icon="list-alt" class="me-2" />
                      Benutzerfelder verstehen
                    </h5>
                    <p class="card-text">
                      Benutzerfelder sind individuelle Informationen, die du von jedem Bereichsmitglied sammeln möchtest. 
                      Diese werden automatisch in Quittungen eingefügt und ermöglichen eine zentrale Datenverwaltung.
                    </p>
                    
                    <div class="row g-4">
                      <div class="col-md-12">
                        <h6 class="fw-bold text-primary mb-3">
                          <font-awesome-icon icon="cog" class="me-2" />
                          Feldtypen
                        </h6>
                        <div class="d-flex flex-wrap gap-3 justify-content-center">
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="font" class="me-1" />
                            Text
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="envelope" class="me-1" />
                            E-Mail
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="calendar" class="me-1" />
                            Datum
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="clock" class="me-1" />
                            Zeit
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="hashtag" class="me-1" />
                            Zahl
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="phone" class="me-1" />
                            Telefon
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="check-square" class="me-1" />
                            Checkbox
                          </span>
                          <span class="badge bg-primary">
                            <font-awesome-icon icon="list" class="me-1" />
                            Auswahl
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            
            </div>

            <!-- Step 3: Receipt Wizard -->
            <div v-if="currentStep === 2" class="gs-step-content">
              <div class="text-center mb-4">
                <font-awesome-icon icon="receipt" size="4x" class="text-primary mb-3" />
                <h2>Der Quittungs Wizard</h2>
                <p class="lead">
                  Erstelle Quittungen Schritt für Schritt mit unserem benutzerfreundlichen Wizard
                </p>
              </div>

              <div class="gs-wizard-steps mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <div class="gs-wizard-step">
                      <div class="gs-wizard-step-number">1</div>
                      <h6>Typ wählen</h6>
                      <p class="small text-muted">Persönlich oder Bereich-Quittung</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="gs-wizard-step">
                      <div class="gs-wizard-step-number">2</div>
                      <h6>Template</h6>
                      <p class="small text-muted">Vorlage auswählen</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="gs-wizard-step">
                      <div class="gs-wizard-step-number">3</div>
                      <h6>Ausfüllen</h6>
                      <p class="small text-muted">Daten eingeben</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="gs-wizard-step">
                      <div class="gs-wizard-step-number">4</div>
                      <h6>Fertig</h6>
                      <p class="small text-muted">Unterschrift & Vorschau</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row g-4">
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="user" class="me-2 text-primary" />Persönliche Quittungen</h5>
                    <p>Erstelle Quittungen für private Zwecke</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Verwende deine persönlichen Quittungen</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Das Standard-Templates ist verfügbar</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Direkte Erstellung möglich</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="users" class="me-2 text-success" />Bereich-Quittungen</h5>
                    <p>Erstelle Quittungen im Bereich-Kontext</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Bereich-Templates und -Felder</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Automatische Quittungsnummern</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Zentrale Verwaltung</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: Pro-Bereich Upgrade -->
            <div v-if="currentStep === 3" class="gs-step-content">
                <div class="text-center mb-4">
                <font-awesome-icon icon="crown" size="4x" class="text-warning mb-3" />
                <h2>Pro-Bereich Upgrade</h2>
                <p class="lead">
                    Erweitere deine Bereich-Funktionen mit individuellen Templates, Logo-Branding und erweiterten Verwaltungsoptionen.
                </p>
              </div>

              <div class="row g-4 mb-4">
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="paint-brush" class="me-2 text-primary" />Individuelle Design</h5>
                    <p>Erstelle maßgeschneiderte Quittungen für verschiedene Sportarten</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Standard-Templates</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Basketball-Schiedsrichter</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Fußball-Schiedsrichter</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Eigene Designs</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="gs-info-card">
                    <h5><font-awesome-icon icon="cogs" class="me-2 text-success" />Erweiterte Funktionen</h5>
                    <p>Professionelle Tools für komplexe Anforderungen</p>
                    <ul class="list-unstyled">
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Logo & Branding</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Erstattungsverwaltung</li>
                      <li><font-awesome-icon icon="check" class="text-success me-2" />Batch Export</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Vorlagen & Export -->
              <div class="mt-4">
                <h5 class="mb-3">
                  <font-awesome-icon icon="paint-brush" class="me-2 text-info" />
                  Designs
                </h5>
                
                <!-- Vorlagen-Beispiele -->
                <div class="row g-3 mb-4">
                  <div class="col-md-3">
                    <div class="card border-primary">
                      <div class="card-body text-center">
                        <font-awesome-icon icon="file-alt" size="2x" class="text-primary mb-2" />
                        <h6>Standard</h6>
                        <p class="small text-muted">Einfache Quittungen für allgemeine Zwecke</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card border-success">
                      <div class="card-body text-center">
                        <font-awesome-icon icon="basketball-ball" size="2x" class="text-success mb-2" />
                        <h6>Basketball</h6>
                        <p class="small text-muted">Schiedsrichter-Quittungen</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card border-warning">
                      <div class="card-body text-center">
                        <font-awesome-icon icon="futbol" size="2x" class="text-warning mb-2" />
                        <h6>Fußball</h6>
                        <p class="small text-muted">Schiedsrichter-Quittungen</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card border-dark">
                      <div class="card-body text-center">
                        <font-awesome-icon icon="futbol" size="2x" class="text-dark mb-2" />
                        <h6>Advanced</h6>
                        <p class="small text-muted">Individualisierbare Quittung für jeden Einsatz</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Erweiterte Funktionen Cards -->
                <div class="row g-4 mb-4">
                  <div class="col-md-6">
                    <div class="gs-info-card">
                      <h5><font-awesome-icon icon="money-bill-wave" class="me-2 text-success" />Erstattungsverwaltung</h5>
                      <p>Automatisierte Erstattungsberechnung und Zahlungsabwicklung</p>
                      <ul class="list-unstyled">
                        <li><font-awesome-icon icon="check" class="text-success me-2" />Automatische Berechnung</li>
                        <li><font-awesome-icon icon="check" class="text-success me-2" />PayPal.me Integration</li>
                        <li><font-awesome-icon icon="check" class="text-success me-2" />SEPA XML Export</li>
                        <li><font-awesome-icon icon="check" class="text-success me-2" />Überweisungslisten</li>
                      </ul>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="gs-info-card">
                      <h5><font-awesome-icon icon="download" class="me-2 text-info" />Batch Export</h5>
                      <p>Effiziente Verarbeitung und Export mehrerer Quittungen</p>
                      <ul class="list-unstyled">
                        <li><font-awesome-icon icon="check" class="text-success me-2" />Mehrere Quittungen gleichzeitig</li>
                        <li><font-awesome-icon icon="check" class="text-success me-2" />Verschiedene Formate</li>
                        <li><font-awesome-icon icon="check" class="text-success me-2" />Zahlungsstatus</li>

                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Vorausgefüllte Quittungen -->
                <div class="alert alert-info">
                  <h6 class="fw-bold mb-2">
                    <font-awesome-icon icon="magic" class="me-2" />
                    Vorausgefüllte Quittungen
                  </h6>
                  <p class="mb-0">
                    Erstelle Vorlagen mit vorausgefüllten Feldern, die direkt im Quittungs-Wizard verwendet werden können.
                  </p>
                </div>
              </div>

              <!-- Upgrade Info -->
              <div class="text-center mt-4">
                <div class="alert alert-warning d-inline-block">
                  <font-awesome-icon icon="info-circle" class="me-2" />
                  <strong>Wichtiger Hinweis:</strong> Pro-Bereich Upgrades gelten jeweils für 1 Bereich
                </div>
              </div>
            </div>

            <!-- Step 5: Account & Billing -->
            <div v-if="currentStep === 4" class="gs-step-content">
              <div class="text-center mb-4">
                <font-awesome-icon icon="credit-card" size="4x" class="text-warning mb-3" />
                <h2>Account & Quittungen verwalten</h2>
                <p class="lead">
                  Verwalte deine Quittungen, kaufe neue und überwache deine Ausgaben
                </p>
              </div>

              <div class="row g-4">
                <div class="col-md-4">
                  <div class="gs-info-card text-center">
                    <font-awesome-icon icon="coins" size="3x" class="text-warning mb-3" />
                    <h5>Quittungen kaufen</h5>
                    <p class="text-muted">
                      Kaufe zusätzliche Quittungen für persönliche oder Bereich-Accounts
                    </p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="gs-info-card text-center">
                    <font-awesome-icon icon="exchange-alt" size="3x" class="text-info mb-3" />
                    <h5>Transfer zwischen Accounts</h5>
                    <p class="text-muted">
                      Verschiebe Quittungen zwischen persönlichen und Bereich-Accounts
                    </p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="gs-info-card text-center">
                    <font-awesome-icon icon="history" size="3x" class="text-secondary mb-3" />
                    <h5>Verlauf einsehen</h5>
                    <p class="text-muted">
                      Überwache alle Käufe und Transaktionen in der Historie
                    </p>
                  </div>
                </div>
              </div>
 
        
            </div>

            <!-- Step 5: Next Steps -->
            <div v-if="currentStep ===5" class="gs-step-content">
              <div class="text-center mb-4">
                <font-awesome-icon icon="check-circle" size="4x" class="text-success mb-3" />
                <h2>Bereit für den Start!</h2>
                <p class="lead">
                  Du kennst jetzt die Grundlagen. Hier sind die nächsten Schritte:
                </p>
              </div>

              <div class="row g-4 mb-4">
                <div class="col-md-6">
                  <div class="gs-action-card">
                    <h5><font-awesome-icon icon="receipt" class="me-2 text-primary" />Erste Quittung erstellen</h5>
                    <p>Starte mit einer persönlichen Quittung</p>
                    <button @click="createReceipt" class="btn btn-primary">
                      <font-awesome-icon icon="plus" class="me-2" />
                      Quittung erstellen
                    </button>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="gs-action-card">
                    <h5><font-awesome-icon icon="users" class="me-2 text-success" />Bereich erstellen</h5>
                    <p>Lade Kollegen ein und arbeite zusammen</p>
                    <button @click="createTeam" class="btn btn-success">
                      <font-awesome-icon icon="plus" class="me-2" />
                      Bereich erstellen
                    </button>
                  </div>
                </div>
              </div>

              <div class="alert alert-success">
                <h6><font-awesome-icon icon="star" class="me-2" />Weitere Ressourcen</h6>
                <div class="row">
                  <div class="col-md-6">
                    <ul class="list-unstyled mb-0">
                      <li><font-awesome-icon icon="book" class="text-primary me-2" />
                        <button @click="goToDashboard" class="btn btn-link p-0 text-decoration-none">Dashboard erkunden</button>
                      </li>
                      <li><font-awesome-icon icon="list" class="text-primary me-2" />
                        <button @click="goToReceipts" class="btn btn-link p-0 text-decoration-none">Alle Quittungen ansehen</button>
                      </li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <ul class="list-unstyled mb-0">
                      <li><font-awesome-icon icon="credit-card" class="text-primary me-2" />
                        <button @click="goToAccount" class="btn btn-link p-0 text-decoration-none">Account verwalten</button>
                      </li>
                      <li><font-awesome-icon icon="user" class="text-primary me-2" />
                        <button @click="goToProfile" class="btn btn-link p-0 text-decoration-none">Profil bearbeiten</button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation -->
            <div class="d-flex justify-content-between mt-5">
              <button 
                v-if="currentStep > 0"
                @click="previousStep" 
                class="btn btn-outline-secondary"
              >
                <font-awesome-icon icon="arrow-left" class="me-2" />
                Zurück
              </button>
              <div v-else></div>
              
              <button 
                v-if="currentStep < steps.length - 1"
                @click="nextStep" 
                class="btn btn-primary"
              >
                Weiter
                <font-awesome-icon icon="arrow-right" class="ms-2" />
              </button>
              <button 
                v-else
                @click="finishTutorial" 
                class="btn btn-success"
              >
                <font-awesome-icon icon="check" class="me-2" />
                Tutorial beenden
              </button>
            </div>
          </div>
        </div>

        <!-- Skip Tutorial -->
        <div class="text-center mt-4">
          <button @click="skipTutorial" class="btn btn-link text-muted">
            <font-awesome-icon icon="times" class="me-2" />
            Tutorial überspringen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { showSuccess, showError } from '@/services/notification'
import {markTutorialComplete} from '@/api/auth'
const router = useRouter()
const authStore = useAuthStore()

const currentStep = ref(0)

const steps = [
  {
    title: 'Einführung',
    description: 'Was ist Quitly?'
  },
  {
    title: 'Bereiche',
    description: 'Kollaboration verstehen'
  },
  {
    title: 'Quittungs Wizard',
    description: 'Quittungen erstellen'
  },
  {
    title: 'Pro-Bereich',
    description: 'Erweiterte Funktionen'
  },
  {
    title: 'Account',
    description: 'Verwaltung & Billing'
  },
  {
    title: 'Fertig',
    description: 'Bereit für den Start'
  }
]

const nextStep = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const finishTutorial = async () => {
  try {
    
    await markTutorialComplete()
    
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Willkommen bei Quitly!')
    router.push('/dashboard')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const skipTutorial = async () => {
  try {
    
    await markTutorialComplete()
    
    await authStore.init()
    showSuccess('Tutorial übersprungen. Du kannst es jederzeit über das Profil wiederholen.')
    router.push('/dashboard')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const createReceipt = async () => {
  try {
    
    await markTutorialComplete()
    
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zur Quittungserstellung.')
    router.push('/receipts/create')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const createTeam = async () => {
  try {
    
    await markTutorialComplete()
    
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zur Bereich-Erstellung.')
    router.push('/teams/create')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const goToDashboard = async () => {
  try {
    await markTutorialComplete()
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zum Dashboard.')
    router.push('/dashboard')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const goToReceipts = async () => {
  try {
    await markTutorialComplete()
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zu den Quittungen.')
    router.push('/receipts')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const goToAccount = async () => {
  try {
    await markTutorialComplete()
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zur Account-Verwaltung.')
    router.push('/account')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

const goToProfile = async () => {
  try {
    await markTutorialComplete()
    await authStore.init()
    showSuccess('Tutorial abgeschlossen! Weiter zur Profil-Bearbeitung.')
    router.push('/profile')
  } catch (error) {
    console.error('Error marking tutorial complete:', error)
    showError('Fehler beim Speichern des Tutorial-Status')
  }
}

onMounted(() => {
  
  if (!authStore.isAuthenticated) {
    router.push('/auth/login')
  }
})
</script>

<style scoped>
:root {
  --bs-primary-rgb: 13, 110, 253;
}
/* Main Container */
.getting-started-page {
  min-height: 100vh;
  padding: 2rem 0;
}

/* Header Styling */
.getting-started-page .display-4 {
  background: var(--bs-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
}

.getting-started-page .lead {
  font-size: 1.25rem;
  font-weight: 400;
  color: #6c757d;
}

/* Progress Steps */
.gs-progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  padding: 1rem 0;
}

.gs-progress-steps::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 50px;
  right: 50px;
  height: 3px;
  background: #e9ecef;
  z-index: 1;
  border-radius: 2px;
}

.gs-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 200px;
  padding: 0 1rem;
}

.gs-step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 3px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.gs-step.gs-active .gs-step-circle {
  background: var(--bs-primary);
  color: white;
  border-color: #fff;
  box-shadow: 0 4px 15px rgba(var(--bs-primary-rgb), 0.3);
  transform: scale(1.1);
}

.gs-step.gs-completed .gs-step-circle {
  background: #198754;
  color: white;
  border-color: #fff;
  box-shadow: 0 4px 15px rgba(25, 135, 84, 0.3);
}

.gs-step.gs-disabled .gs-step-circle {
  background: #f8f9fa;
  color: #adb5bd;
  border-color: #e9ecef;
}

.gs-step-number {
  font-weight: 700;
  font-size: 18px;
}

.gs-step-icon {
  font-size: 16px;
}

.gs-step-title {
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 16px;
  color: #495057;
}

.gs-step-description {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

/* Cards */
.gs-feature-card, .gs-info-card, .gs-action-card {
  padding: 2.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.gs-feature-card::before, .gs-info-card::before, .gs-action-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--bs-primary);
}

.gs-feature-card:hover, .gs-info-card:hover, .gs-action-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border-color: rgba(13, 110, 253, 0.2);
}

.gs-feature-card h5, .gs-info-card h5, .gs-action-card h5 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 1rem;
}

.gs-feature-card p, .gs-info-card p, .gs-action-card p {
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

/* Wizard Steps */
.gs-wizard-steps {
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
}

.gs-wizard-step {
  text-align: center;
  padding: 1.5rem 1rem;
  transition: all 0.3s ease;
}

.gs-wizard-step:hover {
  transform: translateY(-2px);
}

.gs-wizard-step .gs-wizard-step-number {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--bs-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 20px;
  margin: 0 auto 1.5rem;
  box-shadow: 0 4px 15px rgba(var(--bs-primary-rgb), 0.3);
  transition: all 0.3s ease;
}

.gs-wizard-step:hover .gs-wizard-step-number {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(13, 110, 253, 0.4);
}

.gs-wizard-step h6 {
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.gs-wizard-step .small {
  color: #6c757d;
  line-height: 1.4;
}

/* Step Content */
.gs-step-content {
  padding: 1rem 0;
}

/* Alerts */
.alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.alert-gradient-warning {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  color: #856404;
}

.alert-gradient-info {
  background: #d1ecf1;
  border-left: 4px solid #17a2b8;
  color: #0c5460;
}

.alert h6 {
  font-weight: 700;
  margin-bottom: 0.5rem;
}

/* Buttons */
.btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  background: var(--bs-primary);
  border: none;
}

.btn-success {
  background: #198754;
  border: none;
}

.btn-outline-secondary {
  border: 2px solid #6c757d;
  color: #6c757d;
  background: transparent;
}

.btn-outline-secondary:hover {
  background: #6c757d;
  color: white;
}

.btn-outline-info {
  border: 2px solid #17a2b8;
  color: #17a2b8;
  background: transparent;
}

.btn-outline-info:hover {
  background: #17a2b8;
  color: white;
}

.btn-link {
  text-decoration: none;
  color: #6c757d;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-link:hover {
  text-decoration: none;
  color: var(--bs-primary);
  transform: translateX(5px);
}

/* Lists */
.list-unstyled li {
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.list-unstyled li:hover {
  background: rgba(13, 110, 253, 0.05);
  padding-left: 1rem;
  border-radius: 8px;
}

.list-unstyled li:last-child {
  border-bottom: none;
}

/* Icons */
.text-primary {
  color: var(--bs-primary) !important;
}

.text-success {
  color: #198754 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .getting-started-page {
    padding: 1rem 0;
  }
  
  .gs-progress-steps {
    flex-direction: column;
    gap: 2rem;
    padding: 2rem 0;
  }
  
  .gs-progress-steps::before {
    display: none;
  }
  
  .gs-step {
    flex-direction: row;
    text-align: left;
    max-width: none;
    padding: 1rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
  
  .gs-step-circle {
    margin-right: 1.5rem;
    margin-bottom: 0;
    flex-shrink: 0;
  }
  
  .gs-step-content {
    min-height: auto;
    padding: 0.5rem 0;
  }
  
  .gs-feature-card, .gs-info-card, .gs-action-card {
    padding: 1.5rem;
    margin-bottom: 1rem;
  }
  
  .gs-wizard-steps {
    padding: 1.5rem;
  }
  
  .gs-wizard-step {
    padding: 1rem 0.5rem;
  }
  
  .gs-wizard-step .gs-wizard-step-number {
    width: 50px;
    height: 50px;
    font-size: 18px;
  }
}

@media (max-width: 576px) {
  .getting-started-page .display-4 {
    font-size: 2rem;
  }
  
  .getting-started-page .lead {
    font-size: 1.1rem;
  }
  
  .gs-step-circle {
    width: 40px;
    height: 40px;
  }
  
  .gs-step-number {
    font-size: 16px;
  }
  
  .gs-wizard-step .gs-wizard-step-number {
    width: 45px;
    height: 45px;
    font-size: 16px;
  }
}

/* Animation for step transitions */
.gs-step-content {
  animation: gs-fadeInUp 0.5s ease-out;
}

@keyframes gs-fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Card hover effects */
.gs-feature-card:hover .text-primary,
.gs-info-card:hover .text-primary,
.gs-action-card:hover .text-primary {
  color: #6f42c1 !important;
}

/* Progress bar animation */
.gs-step.gs-completed .gs-step-circle {
  animation: gs-pulse 0.6s ease-in-out;
}

@keyframes gs-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
